import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

const NAV_MAP: Record<string, string> = {
  d: '/dashboard',
  o: '/orders',
  p: '/products',
  u: '/users/members',
  c: '/coupons',
  e: '/cms/events',
  n: '/cms/popups',
  l: '/claims',
};

const GO_RESET_MS = 1500;

interface UseGlobalShortcutsOptions {
  onCommandPalette: () => void;
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
};

export const useGlobalShortcuts = ({ onCommandPalette }: UseGlobalShortcutsOptions) => {
  const navigate = useNavigate();
  const goPrefixActiveRef = useRef(false);
  const goTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const cancelGoMode = () => {
      goPrefixActiveRef.current = false;
      if (goTimeoutRef.current != null) {
        window.clearTimeout(goTimeoutRef.current);
        goTimeoutRef.current = null;
      }
    };

    const armGoMode = () => {
      goPrefixActiveRef.current = true;
      if (goTimeoutRef.current != null) {
        window.clearTimeout(goTimeoutRef.current);
      }
      goTimeoutRef.current = window.setTimeout(cancelGoMode, GO_RESET_MS);
    };

    const handler = (event: KeyboardEvent) => {
      // Cmd/Ctrl + K — always
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        cancelGoMode();
        onCommandPalette();
        return;
      }

      // ignore G-prefix when inside editable elements or with modifiers
      if (isEditableTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const key = event.key.toLowerCase();

      if (goPrefixActiveRef.current) {
        const path = NAV_MAP[key];
        if (path) {
          event.preventDefault();
          cancelGoMode();
          navigate(path);
          return;
        }
        // any other key cancels go-mode
        cancelGoMode();
        return;
      }

      if (key === 'g') {
        armGoMode();
        return;
      }
    };

    const openEventHandler = () => {
      cancelGoMode();
      onCommandPalette();
    };

    window.addEventListener('keydown', handler);
    window.addEventListener('open-command-palette', openEventHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('open-command-palette', openEventHandler);
      cancelGoMode();
    };
  }, [navigate, onCommandPalette]);
};
